// In a call (Stage 4.8f.4a, mockups "In a call" and "Incoming — while you are on a call"): who, how long, recorded; mute, hold, keypad,
// transfer; a second call waiting (the owner's F3) — "hold and answer" puts this one on hold, one tap switches back.
import { useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { Mic, MicOff, Pause, Play, Grid3x3, ArrowRightLeft, PhoneOff, Copy, Ticket, Phone, CircleDot } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CallKey, Chip, Initial } from "./phoneUi";
import { fmtDur, useSince } from "./phoneFormat";
import PhoneTransfer from "./PhoneTransfer";

const PAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const isColleague = (n) => /^\d{2,8}$/.test(String(n ?? ""));

function Held({ c, onResume, nameOf }) {
  const { t } = useTranslation("phone");
  const since = useSince(c.answeredAt ?? c.since);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "8px 10px", borderRadius: "12px", background: "var(--warning-bg)" }}>
      <Pause size={16} strokeWidth={1.8} aria-hidden="true" style={{ color: "var(--warning)" }} />
      <Typography dir="auto" noWrap sx={{ flex: 1, minWidth: 0, font: "600 13px/1.3 var(--font-ui)", color: "var(--ink-800)" }}>{t("onHoldName", { name: nameOf(c) })} · {fmtDur(since)}</Typography>
      <ButtonBase onClick={() => onResume(c.id)} sx={{ minHeight: 36, px: "12px", borderRadius: "10px", background: "var(--surface-card)", border: "1px solid var(--line-200)", font: "700 12px/1 var(--font-ui)" }}>{t("switchTo")}</ButtonBase>
    </Box>
  );
}

function Waiting({ c, onAnswer, onDecline, nameOf }) {
  const { t } = useTranslation("phone");
  const ringing = useSince(c.since);
  return (
    <Box role="alert" sx={{ p: "12px", borderRadius: "14px", border: "2px solid var(--brand-violet)", background: "var(--c-violet-bg)", display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Initial name={nameOf(c)} size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography dir="auto" noWrap sx={{ font: "700 14px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)", color: "var(--ink-900)" }}>{t("secondCall", { name: nameOf(c) })}</Typography>
          <Typography sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-600)" }}>{t("ringingFor", { time: fmtDur(ringing) })}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: "8px" }}>
        <ButtonBase onClick={() => onDecline(c.id)} sx={{ flex: 1, minHeight: 44, borderRadius: "12px", background: "var(--surface-card)", border: "1px solid var(--line-200)", font: "700 13px/1 var(--font-ui)", color: "var(--ink-800)" }}>{t("decline")}</ButtonBase>
        <ButtonBase onClick={() => onAnswer(c.id)} sx={{ flex: 1.4, minHeight: 44, borderRadius: "12px", background: "var(--success)", color: "#fff", font: "700 13px/1 var(--font-ui)" }}>{t("holdAndAnswer")}</ButtonBase>
      </Box>
      <Typography sx={{ font: "500 12px/1.35 var(--font-ui)", color: "var(--ink-600)" }}>{t("waitingExplain")}</Typography>
    </Box>
  );
}

export default function PhoneCall({ line, current, displayName, onOpenTicket }) {
  const { t } = useTranslation("phone");
  const [pad, setPad] = useState(false);
  const [transfer, setTransfer] = useState(false);
  const [typed, setTyped] = useState("");
  const [copied, setCopied] = useState(false);
  const talking = useSince(current?.answeredAt);
  const nameOf = (c) => (c.id === current?.id ? displayName : null) || c.name || c.number || t("unknown");
  const others = line.calls.filter((c) => c.id !== current?.id);
  const held = others.filter((c) => c.state === "held");
  const waiting = others.find((c) => c.state === "ringing");
  if (!current) return null;
  const name = nameOf(current);
  const onHold = current.state === "held";
  const colleague = isColleague(current.number);

  const copy = () => { navigator.clipboard?.writeText(current.number).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); };
  const key = (k) => { line.sendDtmf(k); setTyped((s) => (s + k).slice(-16)); };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box sx={{ p: "12px 16px", display: "flex", flexDirection: "column", gap: "12px", overflowY: "auto" }}>
        {waiting && <Waiting c={waiting} nameOf={nameOf} onAnswer={line.answer} onDecline={line.hangup} />}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {!colleague && current.state !== "calling" && <Chip tone="bad"><CircleDot size={12} aria-hidden="true" style={{ color: "var(--danger)" }} />{t("recording")}</Chip>}
          <Chip>{current.dir === "in" ? t("incoming") : colleague ? t("colleague") : t("outgoing")}</Chip>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Initial name={name} size={52} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography dir="auto" noWrap sx={{ font: "800 18px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)", color: "var(--ink-900)" }}>{name}</Typography>
            {name !== current.number && <Typography noWrap sx={{ font: "600 13px/1.3 var(--font-ui)", color: "var(--ink-600)" }}><span dir="ltr">{current.number}</span></Typography>}
            <Typography aria-live="polite" sx={{ font: "700 14px/1.4 var(--font-ui)", fontVariantNumeric: "tabular-nums", color: onHold ? "var(--warning)" : "var(--ink-600)" }}>
              {current.state === "calling" ? t("calling") : onHold ? t("onHold") : fmtDur(talking)}
            </Typography>
          </Box>
        </Box>

        {held.map((c) => <Held key={c.id} c={c} nameOf={nameOf} onResume={line.resume} />)}

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px" }}>
          <CallKey icon={current.muted ? MicOff : Mic} label={current.muted ? t("unmute") : t("mute")} active={current.muted} disabled={current.state !== "active"} onClick={() => line.toggleMute(current.id)} />
          <CallKey icon={onHold ? Play : Pause} label={onHold ? t("resume") : t("hold")} active={onHold} disabled={current.state === "calling"} onClick={() => (onHold ? line.resume(current.id) : line.hold(current.id))} />
          <CallKey icon={Grid3x3} label={t("keypad")} active={pad} onClick={() => { setPad((v) => !v); setTransfer(false); }} />
          <CallKey icon={ArrowRightLeft} label={t("transfer")} active={transfer} disabled={current.state !== "active" && !onHold} onClick={() => { setTransfer((v) => !v); setPad(false); }} />
        </Box>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <ButtonBase onClick={copy} sx={{ flex: 1, minHeight: 40, gap: "6px", borderRadius: "10px", border: "1px solid var(--line-100)", font: "600 12px/1 var(--font-ui)", color: "var(--ink-800)" }}>
            <Copy size={15} aria-hidden="true" />{copied ? t("copied") : t("copyNumber")}
          </ButtonBase>
          {onOpenTicket && (
            <ButtonBase onClick={() => onOpenTicket(current.number)} sx={{ flex: 1, minHeight: 40, gap: "6px", borderRadius: "10px", border: "1px solid var(--line-100)", font: "600 12px/1 var(--font-ui)", color: "var(--ink-800)" }}>
              <Ticket size={15} aria-hidden="true" />{t("newTicket")}
            </ButtonBase>
          )}
        </Box>

        {pad && (
          <Box>
            <Typography dir="ltr" sx={{ minHeight: 22, textAlign: "center", font: "700 18px/1.2 var(--font-mono)", letterSpacing: "2px", color: "var(--ink-900)" }}>{typed}</Typography>
            <Box role="group" aria-label={t("keypad")} sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
              {PAD.map((k) => (
                <ButtonBase key={k} onClick={() => key(k)} aria-label={k} sx={{ minHeight: 44, borderRadius: "12px", background: "var(--surface-2)", border: "1px solid var(--line-100)", font: "600 18px/1 var(--font-ui)" }}>{k}</ButtonBase>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {transfer && <PhoneTransfer customer={name} onClose={() => setTransfer(false)} />}

      <Box sx={{ p: "10px 16px 14px", mt: "auto" }}>
        <ButtonBase onClick={() => line.hangup(current.id)} sx={{ width: "100%", minHeight: 48, gap: "8px", borderRadius: "12px", background: "var(--danger)", color: "#fff", font: "700 14px/1 var(--font-ui)" }}>
          {current.state === "calling" ? <Phone size={18} aria-hidden="true" /> : <PhoneOff size={18} aria-hidden="true" />}
          {current.state === "calling" ? t("cancelCall") : t("endCall")}
        </ButtonBase>
      </Box>
    </Box>
  );
}
